/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { format } from 'date-fns';
import { EXCEL_YEAR_MONTH_DAY_PATTERN } from 'modules/common/constants/date-range';

// Formats data based on the column type.
export const formatData = (data, type) => {
  if (type === 'number') {
    if (typeof data === 'string' && data?.includes('%')) {
      // strip thousands separators so values such as 4,925.50% are not truncated at the comma
      const numeric = parseFloat(data.replaceAll(',', '').replace('%', ''));
      if (Number.isNaN(numeric)) return data;

      const hasDecimal = !Number.isInteger(numeric);
      return `${numeric?.toLocaleString(
        undefined,
        hasDecimal ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {}
      )}%`;
    }
    if (typeof data === 'string' && data.includes(',')) {
      return data;
    }
    const numeric = parseFloat(data);
    if (Number.isNaN(numeric)) return data;

    const hasDecimal = !Number.isInteger(numeric);
    return numeric?.toLocaleString(
      undefined,
      hasDecimal ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {}
    );
  }

  if (type === 'date') {
    const date = new Date(data);
    if (Number.isNaN(date.getTime())) return data; // Invalid date
    return format(date, EXCEL_YEAR_MONTH_DAY_PATTERN);
  }

  return data;
};

// Recursively builds header rows for nested column definitions.
export const getHeaderRows = (cols, level = 0, rows = []) => {
  rows[level] = rows[level] || [];
  cols?.forEach((col) => {
    rows[level].push(col);
    if (col?.children) getHeaderRows(col?.children, level + 1, rows);
  });
  return rows;
};

// Flattens nested column definitions into a single-level array
export const flattenColumns = (cols) =>
  cols.flatMap((col) => (col.children ? flattenColumns(col.children) : [col]));

// Counts the columns rendered by the header. A group with no children still renders as one column
export const getRenderedColumnCount = (cols) =>
  cols?.reduce(
    (count, col) => count + (col?.children?.length ? getRenderedColumnCount(col.children) : 1),
    0
  ) || 0;

/**
 * Returns true if all metric values are zero, empty, or null.
 * Ignores the `StayDate` and `rn` fields.
 *
 * @param {Array<Object>} data - Array of trend data objects.
 * @returns {boolean} True if all metric values are zero, empty, or null; otherwise, false.
 */
export const areAllValuesZero = (data) =>
  data?.every((item) =>
    Object.entries(item).every(([key, value]) => {
      if (key === 'StayDate' || key === 'rn' || key === 'day_name') {
        return true;
      }

      // Treat null and empty string as zero
      if (value == null || value === '') {
        return true;
      }

      if (typeof value === 'number') {
        return value === 0;
      }

      if (typeof value === 'string' && value.trim().endsWith('%')) {
        return parseFloat(value) === 0;
      }

      return false;
    })
  );
// The no-result message is only shown once loading has completed
export const shouldShowNoResult = (rows, isLoadingData) =>
  !isLoadingData &&
  (!rows?.filter((row) => row?.StayDate !== 'Total')?.length || areAllValuesZero(rows));
// Recursively flattens nested data structures into a single-level array
export const flattenData = (data) =>
  data.flatMap(({ children, ...parent }) => [parent, ...(children ? flattenData(children) : [])]);
// Parent (total) rows stay bold whether expanded or collapsed, matching PBI
export const getBodyRowFontWeight = (row) => (row?.children ? 'bold' : 'normal');
export const getCellTextColor = (column, value, primaryTextColor, secondaryTextColor) => {
  // Extract, strip '%', and safely default to an empty string if null/undefined
  const rawValue = value?.toString().replace('%', '') ?? '';

  // Parse the cleaned string, returning NaN if empty or invalid
  const numericValue = parseFloat(rawValue);
  if (!column?.disableNegativeColor && numericValue < 0) return secondaryTextColor;
  return primaryTextColor;
};
